export const REFERENCE_TO_VIDEO_IMAGE_MAX = 9;
export const REFERENCE_TO_VIDEO_VIDEO_MAX = 3;
export const REFERENCE_TO_VIDEO_AUDIO_MAX = 3;
export const REFERENCE_TO_VIDEO_MAX_FILES = 12;
export const REFERENCE_TO_VIDEO_IMAGE_MAX_BYTES = 30 * 1024 * 1024;
export const REFERENCE_TO_VIDEO_VIDEO_MAX_BYTES = 50 * 1024 * 1024;
export const REFERENCE_TO_VIDEO_AUDIO_MAX_BYTES = 15 * 1024 * 1024;
export const REFERENCE_TO_VIDEO_MIN_MEDIA_SECONDS = 2;
export const REFERENCE_TO_VIDEO_MAX_MEDIA_SECONDS = 15;
export const REFERENCE_TO_VIDEO_FREE_IMAGE_COUNT = 5;
export const REFERENCE_TO_VIDEO_EXTRA_IMAGE_KIE_CREDITS = 4;

export const REFERENCE_TO_VIDEO_IMAGE_ACCEPT =
  'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
export const REFERENCE_TO_VIDEO_VIDEO_ACCEPT =
  'video/mp4,video/quicktime,.mp4,.mov';
export const REFERENCE_TO_VIDEO_AUDIO_ACCEPT =
  'audio/mpeg,audio/wav,audio/x-wav,audio/wave,.mp3,.wav';

const REFERENCE_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);
const REFERENCE_VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime']);
const REFERENCE_AUDIO_TYPES = new Set([
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
]);

export function isReferenceToVideoImageType(contentType: string) {
  return REFERENCE_IMAGE_TYPES.has(contentType);
}

export function isReferenceToVideoVideoType(contentType: string) {
  return REFERENCE_VIDEO_TYPES.has(contentType);
}

export function isReferenceToVideoAudioType(contentType: string) {
  return REFERENCE_AUDIO_TYPES.has(contentType);
}

export function validateReferenceToVideoImageFile(file: {
  name: string;
  size: number;
  type: string;
}) {
  if (
    !isReferenceToVideoImageType(file.type) &&
    !/\.(jpe?g|png|webp)$/i.test(file.name)
  ) {
    throw new Error('Choose a JPEG, PNG, or WebP image.');
  }
  if (file.size > REFERENCE_TO_VIDEO_IMAGE_MAX_BYTES) {
    throw new Error('Images must be smaller than 30MB.');
  }
}

export function validateReferenceToVideoVideoFile(file: {
  name: string;
  size: number;
  type: string;
}) {
  if (
    !isReferenceToVideoVideoType(file.type) &&
    !/\.(mp4|mov)$/i.test(file.name)
  ) {
    throw new Error('Choose an MP4 or MOV video.');
  }
  if (file.size > REFERENCE_TO_VIDEO_VIDEO_MAX_BYTES) {
    throw new Error('Videos must be smaller than 50MB.');
  }
}

export function validateReferenceToVideoAudioFile(file: {
  name: string;
  size: number;
  type: string;
}) {
  if (
    !isReferenceToVideoAudioType(file.type) &&
    !/\.(mp3|wav)$/i.test(file.name)
  ) {
    throw new Error('Choose an MP3 or WAV audio file.');
  }
  if (file.size > REFERENCE_TO_VIDEO_AUDIO_MAX_BYTES) {
    throw new Error('Audio files must be smaller than 15MB.');
  }
}

export function assertReferenceToVideoMediaDuration(duration: number) {
  if (
    duration < REFERENCE_TO_VIDEO_MIN_MEDIA_SECONDS ||
    duration > REFERENCE_TO_VIDEO_MAX_MEDIA_SECONDS
  ) {
    throw new Error('Each reference clip must be between 2 and 15 seconds.');
  }
}

export function assertReferenceToVideoInputCounts({
  audioCount,
  imageCount,
  videoCount,
}: {
  audioCount: number;
  imageCount: number;
  videoCount: number;
}) {
  if (imageCount < 0 || imageCount > REFERENCE_TO_VIDEO_IMAGE_MAX) {
    throw new Error(
      `Reference to Video supports up to ${REFERENCE_TO_VIDEO_IMAGE_MAX} images.`
    );
  }
  if (videoCount < 0 || videoCount > REFERENCE_TO_VIDEO_VIDEO_MAX) {
    throw new Error(
      `Reference to Video supports up to ${REFERENCE_TO_VIDEO_VIDEO_MAX} videos.`
    );
  }
  if (audioCount < 0 || audioCount > REFERENCE_TO_VIDEO_AUDIO_MAX) {
    throw new Error(
      `Reference to Video supports up to ${REFERENCE_TO_VIDEO_AUDIO_MAX} audio files.`
    );
  }
  if (imageCount + videoCount + audioCount > REFERENCE_TO_VIDEO_MAX_FILES) {
    throw new Error(
      `Reference to Video supports up to ${REFERENCE_TO_VIDEO_MAX_FILES} files in total.`
    );
  }
  if (imageCount + videoCount < 1) {
    throw new Error('Upload at least one reference image or video.');
  }
  if (audioCount > 0 && imageCount + videoCount < 1) {
    throw new Error('Audio references also need an image or video.');
  }
}
